<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          Quality Check
        </h1>
      </template>
    </page-header>

    <div v-if="!loading">
      <div class="mb-2" v-if="isRectification">
        <span class="badge badge-warning">QC Rectification</span>
      </div>

      <div class="card mb-4">
        <div class="card-body text-center">
          <template v-if="!completed && !completing">
            <template v-if="qc?.vehicle">
              <strong>Vehicle</strong>
              <br />
              {{ vehicleLabel }}
              <br />
            </template>

            <template v-if="technician">
              PDI Technician: {{ technician }}
              <br />
            </template>

            QC Number: {{ qc.qc_number }}
            <br />

            <span class="mr-3" v-if="qc.pdi">
              PDI Number:
              <router-link
                class="font-weight-bold"
                style="text-decoration: underline;"
                :to="`/pdi-queue/${qc.pdi.identifier}`"
                target="_blank"
              >
                {{ qc.pdi.pdi_number }}
              </router-link>
            </span>
            <span v-if="qc.previousQc">
              Previous QC Number:
              <router-link
                class="font-weight-bold"
                style="text-decoration: underline;"
                :to="`/qc-queue/${qc.previousQc.identifier}`"
                target="_blank"
              >
                {{ qc.previousQc.qc_number }}
              </router-link>
            </span>

            <template v-if="qc.completed_at">
              <p class="mt-3">
                <span class="badge" :class="outcomeClass(qc.outcome)">
                  {{ outcomeLabel(qc.outcome) }}
                </span>
                <br />
                QC completed by {{ qc.completedBy?.full_name }} at
                {{ qc.completed_at | date }}
              </p>

              <div v-if="qc.failure_reasons">
                <strong>Failure reasons</strong>
                <br />
                <div
                  class="d-inline-block text-left"
                  v-html="qc.failure_reasons"
                ></div>
              </div>

              <form-button
                level="primary"
                :indicator="downloading"
                @clicked="downloadStatement()"
              >
                Download PDF
              </form-button>
            </template>
          </template>

          <template v-if="completed && !completing">
            <br />
            <p v-if="outcome === 'pass'">
              The QC has passed. The vehicle has moved to the fleet as idle.
            </p>
            <p v-else>
              The QC has failed. The vehicle has been returned to the PDI queue
              for rectification.
            </p>
            <button type="button" class="btn btn-primary" @click="goToQueue()">
              Back To QC Queue
            </button>
          </template>

          <template v-if="completing">
            <preloader></preloader>
          </template>
        </div>
      </div>

      <div class="bail-integration">
        <statement-renderer
          id="statement-renderer"
          :editable="editable"
          :statement="statement"
          :base-url="aisApiUrl"
          navigation="steps"
          @completed="statementCompleted()"
          v-if="!completed && statement"
        ></statement-renderer>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import PageHeader from './../Shared/PageHeader.vue';

export default {
  components: {
    PageHeader
  },

  props: {
    id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: true,
      qc: null,
      statement: null,
      editable: true,
      downloading: false,
      completed: false,
      completing: false,
      outcome: null
    };
  },

  computed: {
    aisApiUrl() {
      return window.AxisUK.ais.api;
    },

    technician() {
      return this.qc?.pdi?.completed_by?.full_name;
    },

    vehicleLabel() {
      const vehicle = this.qc?.vehicle;

      return [vehicle?.make?.label, vehicle?.model, vehicle?.registration]
        .filter(Boolean)
        .join(' - ');
    },

    isRectification() {
      return (
        !!this.qc?.previous_qc_id || this.qc?.status === 'qc_rectification'
      );
    }
  },

  mounted() {
    Promise.all([
      window.Ajax.get(
        `qc/${this.id}?parts=vehicle,abiGroup,pdi,completedBy,previousQc`
      ),
      window.Ajax.get(`qc/${this.id}/statement`)
    ])
      .then(([qc, statement]) => {
        this.qc = qc.data;
        this.statement = JSON.stringify(statement.data);

        if (statement.data.completed_at) {
          this.editable = false;
        }

        this.loading = false;
      })
      .catch(error => {
        if (error?.response?.status === 403) {
          window.toastify.error(
            'Not allowed',
            'You do not have permission to perform this QC. It cannot be done by the technician who completed the PDI.'
          );

          this.route('/qc-queue');

          return;
        }

        window.toastify.error('Oops', 'An error occurred while loading the QC');
      });
  },

  methods: {
    outcomeLabel(outcome) {
      return outcome === 'pass' ? 'Passed' : 'Failed';
    },

    outcomeClass(outcome) {
      return outcome === 'pass' ? 'badge-success' : 'badge-danger';
    },

    downloadStatement() {
      this.downloading = true;

      window.Ajax.download(`qc/${this.id}/download`)
        .then(() => (this.downloading = false))
        .catch(() => {
          this.downloading = false;

          window.toastify.error(
            'Oops',
            'An error occurred while downloading the PDF'
          );
        });
    },

    statementCompleted() {
      this.completing = true;

      window.Ajax.post(`qc/${this.id}/complete`)
        .then(response => {
          this.outcome = response.data.outcome;
          this.completed = true;
          this.completing = false;
        })
        .catch(error => {
          this.completing = false;

          window.toastify.error(
            'Oops',
            error?.response?.data?.message ||
              'An error occurred while completing the QC'
          );
        });
    },

    goToQueue() {
      this.route('/qc-queue');
    }
  }
};
</script>
